import { useState } from 'react';
import { track } from '@/lib/analytics';
import {
  SUBSCRIPTION_PLANS,
  BillingCycle,
  planMonthlyCents,
  addonMonthlyCents,
  perImageLabel,
  formatEuro,
} from '@/lib/stripeConfig';
import {
  EARLY_BIRD,
  earlyBirdSpotsLeft,
  earlyBirdProgressPercent,
  earlyBirdOriginalCents,
} from '@/lib/earlyBird';

interface PricingSectionProps {
  onCtaClick: () => void;
}

export const PricingSection: React.FC<PricingSectionProps> = ({ onCtaClick }) => {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [addonChecked, setAddonChecked] = useState<Record<string, boolean>>({});

  const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  );

  const StarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09Z"/>
    </svg>
  );

  const handleCycleChange = (next: BillingCycle) => {
    setCycle(next);
    track('Landing Pricing Toggle', { billing_cycle: next });
  };

  return (
    <section className="lp-pricing-section" id="pricing">
      <div className="lp-pricing-inner">
        <div className="lp-section-heading-center">
          <div className="lp-pricing-early-bird-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
            {EARLY_BIRD.enabled
              ? `Early-Bird: 50 % Rabatt für die ersten ${EARLY_BIRD.totalSpots} Nutzer`
              : 'Jährlich zahlen = 2 Monate gratis'}
          </div>
          <h2>Jeden Monat frische Credits</h2>
          <p>Bild-Abo für alle. Das Grundriss-Paket klickst du bei Bedarf einfach dazu, der Preis passt sich sofort an. 1 Credit = 1 generiertes Bild, und mit 10 Credits stagst du locker eine komplette Immobilie.</p>
        </div>

        <div className="lp-pricing-toggle">
          <div className="lp-pricing-toggle-wrapper" role="group" aria-label="Abrechnung">
            <button
              type="button"
              className={`lp-pricing-toggle-btn${cycle === 'monthly' ? ' active' : ''}`}
              onClick={() => handleCycleChange('monthly')}
            >
              Monatlich
            </button>
            <button
              type="button"
              className={`lp-pricing-toggle-btn${cycle === 'yearly' ? ' active' : ''}`}
              onClick={() => handleCycleChange('yearly')}
            >
              Jährlich <span className="lp-pricing-toggle-save">2 Monate gratis</span>
            </button>
          </div>
        </div>

        {EARLY_BIRD.enabled && (
          <div className="lp-pricing-spots">
            <div className="lp-pricing-spots-header">
              <span className="lp-pricing-spots-count">
                <strong>{EARLY_BIRD.takenSpots}</strong> von {EARLY_BIRD.totalSpots} Early-Bird-Plätzen vergeben
              </span>
              <span className="lp-pricing-spots-left">Nur noch {earlyBirdSpotsLeft()} frei</span>
            </div>
            <div className="lp-pricing-spots-bar" role="progressbar" aria-valuenow={EARLY_BIRD.takenSpots} aria-valuemin={0} aria-valuemax={EARLY_BIRD.totalSpots}>
              <div className="lp-pricing-spots-fill" style={{ width: `${earlyBirdProgressPercent()}%` }} />
            </div>
          </div>
        )}

        <div className="lp-pricing-cards">
          {SUBSCRIPTION_PLANS.map((plan) => {
            const withAddon = !!addonChecked[plan.id];
            const baseCents = planMonthlyCents(plan, cycle);
            const addonCents = addonMonthlyCents(plan, cycle);
            const totalCents = baseCents + (withAddon ? addonCents : 0);

            return (
              <div key={plan.id} className={`lp-pricing-card${plan.popular ? ' featured' : ''}`}>
                {plan.popular && (
                  <div className="lp-pricing-popular-badge">
                    Empfohlen <StarIcon />
                  </div>
                )}
                <div className="lp-pricing-header">
                  <div className="lp-pricing-header-row">
                    <div>
                      <div className="lp-pricing-plan-name">{plan.name}</div>
                      <div className="lp-pricing-plan-subtitle">{plan.tagline}</div>
                    </div>
                    <span className="lp-pricing-savings">🏠 {plan.immoLabel}</span>
                  </div>
                </div>
                <div className="lp-pricing-price">
                  {EARLY_BIRD.enabled && (
                    <div className="lp-pricing-price-was">
                      <span className="lp-pricing-original">{formatEuro(earlyBirdOriginalCents(totalCents))}</span>
                      <span className="lp-pricing-discount-chip">{EARLY_BIRD.discountLabel}</span>
                    </div>
                  )}
                  <div className="lp-pricing-price-now">
                    <span className="lp-pricing-amount">{formatEuro(totalCents)}</span>
                    <span className="lp-pricing-interval">/ Monat</span>
                  </div>
                </div>
                <div className="lp-pricing-footer-note">
                  {cycle === 'yearly' ? 'pro Monat · jährlich abgerechnet' : ' '}
                  {withAddon && <span className="lp-pricing-incl-addon"> inkl. Grundriss-Paket</span>}
                </div>
                <div className="lp-pricing-credits">
                  <span className="lp-pricing-credits-count">{plan.imageCredits} Bild-Credits / Monat</span>
                  <span className="lp-pricing-credits-rate">({perImageLabel(plan, cycle)})</span>
                </div>
                <div className="lp-pricing-features">
                  {plan.features.map((f) => (
                    <div key={f} className="lp-pricing-feature">
                      <CheckIcon />
                      {f}
                    </div>
                  ))}
                </div>
                <label className={`lp-pricing-addon${withAddon ? ' on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={withAddon}
                    onChange={(e) => {
                      setAddonChecked((prev) => ({ ...prev, [plan.id]: e.target.checked }));
                      track('Landing Addon Toggled', { plan: plan.id, enabled: e.target.checked });
                    }}
                  />
                  <span className="lp-pricing-addon-text">
                    <span className="lp-pricing-addon-title">+ Grundriss-Paket</span>
                    <span className="lp-pricing-addon-desc">
                      {plan.addon.floorplansPerMonth} Grundriss{plan.addon.floorplansPerMonth !== 1 ? 'e' : ''}/Mo · 2D + 3D · Nachbessern gratis
                    </span>
                  </span>
                  <span className="lp-pricing-addon-price">
                    {EARLY_BIRD.enabled && (
                      <span className="lp-pricing-addon-original">+{formatEuro(earlyBirdOriginalCents(addonCents))}</span>
                    )}
                    {' '}+{formatEuro(addonCents)}
                  </span>
                </label>
                <div className="lp-pricing-footer">
                  <button
                    className="lp-btn-tertiary"
                    onClick={() => { track('Landing CTA Clicked', { location: 'pricing', plan: plan.name, billing_cycle: cycle, with_floorplan_addon: withAddon }); onCtaClick?.(); }}
                  >
                    {plan.name} wählen
                  </button>
                  <div className="lp-pricing-footer-note">Monatlich kündbar</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="lp-pricing-footer-note" style={{ textAlign: 'center', marginTop: '24px' }}>
          Sichere Zahlung via Stripe · Alle Preise netto zzgl. MwSt.
        </div>

        <div className="lp-pricing-partners">
          <div className="lp-hero-partners-logos">
            <div className="lp-hero-partners-logos-track">
              {/* Set 1 */}
              <img src="/landing/partners/Logo_PNP-Invest.webp" alt="PNP Invest" className="lp-partner-logo-img" />
              <img src="/landing/partners/PNP-Media_Logo_quer_1.webp" alt="PNP Media" className="lp-partner-logo-img" />
              <img src="/landing/partners/Group_427319346.webp" alt="heimatmakler" className="lp-partner-logo-img" />
              <img src="/landing/partners/Logo_Sebastian-Hoeppner-Immo.svg" alt="Sebastian Höppner Immobilien" className="lp-partner-logo-img lp-partner-logo-img--md" />
              <img src="/landing/partners/Logo_Made-in-Ger.webp" alt="Made in Germany" className="lp-partner-logo-img" />
              <img src="/landing/partners/Logo_Accept_black.svg" alt="Accept Immobilien" className="lp-partner-logo-img lp-partner-logo-img--lg" />
              {/* Set 2 (Duplikat für Endlos-Loop) */}
              <img src="/landing/partners/Logo_PNP-Invest.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
              <img src="/landing/partners/PNP-Media_Logo_quer_1.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
              <img src="/landing/partners/Group_427319346.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
              <img src="/landing/partners/Logo_Sebastian-Hoeppner-Immo.svg" alt="" aria-hidden="true" className="lp-partner-logo-img lp-partner-logo-img--md" />
              <img src="/landing/partners/Logo_Made-in-Ger.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
              <img src="/landing/partners/Logo_Accept_black.svg" alt="" aria-hidden="true" className="lp-partner-logo-img lp-partner-logo-img--lg" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
